import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { MapPin, Phone, Search } from "lucide-react";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";

export default function Centers() {
  const [params, setParams] = useSearchParams();
  const [networks, setNetworks] = useState([]);
  const [centers, setCenters] = useState([]);
  const [q, setQ] = useState(params.get("q") || "");
  const [city, setCity] = useState(params.get("city") || "");
  const [postal, setPostal] = useState(params.get("postal") || "");
  const [network, setNetwork] = useState(params.get("network") || "all");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/networks").then((r) => setNetworks(r.data.networks || []));
  }, []);

  const fetchCenters = () => {
    setLoading(true);
    api
      .get("/centers", {
        params: {
          q: q || undefined,
          city: city || undefined,
          postal_code: postal || undefined,
          network: network !== "all" ? network : undefined,
          limit: 200,
        },
      })
      .then((r) => setCenters(r.data.centers || []))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchCenters(); /* eslint-disable-next-line */ }, []);

  const onSearch = (e) => {
    e.preventDefault();
    const p = {};
    if (q) p.q = q;
    if (city) p.city = city;
    if (postal) p.postal = postal;
    if (network !== "all") p.network = network;
    setParams(p);
    fetchCenters();
  };

  const netName = (slug) => networks.find((n) => n.slug === slug)?.name || slug;

  return (
    <div>
      <section className="hero-bg py-16">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="text-gold uppercase tracking-widest text-sm font-semibold">Annuaire des centres</div>
          <h1 className="font-heading text-4xl sm:text-5xl text-primary-blue mt-3">Trouver un centre auditif près de chez vous</h1>

          <form onSubmit={onSearch} className="mt-8 bg-white p-5 rounded-2xl border border-slate-200 card-lux grid md:grid-cols-5 gap-4">
            <div className="md:col-span-2">
              <Label className="text-sm">Recherche libre</Label>
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nom du centre, adresse…" className="mt-1 h-11" data-testid="centers-search-q" />
            </div>
            <div>
              <Label className="text-sm">Ville</Label>
              <Input value={city} onChange={(e) => setCity(e.target.value)} placeholder="Paris" className="mt-1 h-11" data-testid="centers-search-city" />
            </div>
            <div>
              <Label className="text-sm">Code postal</Label>
              <Input value={postal} onChange={(e) => setPostal(e.target.value)} placeholder="75015" className="mt-1 h-11" data-testid="centers-search-postal" />
            </div>
            <div>
              <Label className="text-sm">Réseau</Label>
              <Select value={network} onValueChange={setNetwork}>
                <SelectTrigger className="mt-1 h-11" data-testid="centers-search-network"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les réseaux</SelectItem>
                  {networks.map((n) => (
                    <SelectItem key={n.slug} value={n.slug}>{n.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-5 flex justify-end">
              <Button type="submit" className="bg-primary-blue text-white" data-testid="centers-search-submit">
                <Search size={18} className="mr-2" /> Rechercher
              </Button>
            </div>
          </form>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-14">
        <div className="text-sm text-slate-500 mb-6" data-testid="centers-result-count">
          {loading ? "Chargement…" : `${centers.length} centre(s) trouvé(s)`}
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {centers.map((c) => (
            <div key={c.id} className="bg-white p-6 rounded-2xl border border-slate-100 card-lux" data-testid={`center-card-${c.id}`}>
              <Badge className="bg-sky-blue text-primary-blue border border-primary-blue/20">{netName(c.network_slug)}</Badge>
              <div className="mt-3 font-heading text-xl text-primary-blue">{c.name}</div>
              <div className="mt-2 text-sm text-slate-600 flex items-start gap-1"><MapPin size={14} className="mt-0.5" /> {c.address}, {c.postal_code} {c.city}</div>
              {c.phone && <div className="mt-1 text-sm text-slate-600 flex items-center gap-1"><Phone size={14} /> {c.phone}</div>}
              <div className="mt-5 flex gap-2">
                <Link to={`/centres/${c.id}`}>
                  <Button variant="outline" className="border-primary-blue text-primary-blue">Voir la fiche</Button>
                </Link>
                <Link to={`/prendre-rendez-vous?network=${c.network_slug}&center=${c.id}`}>
                  <Button className="bg-primary-blue text-white">Rendez-vous</Button>
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
